import { createContext, ReactNode, useContext } from "react";

import { ColumnConfigStore } from "./stores/column-config-store";

// Re-export types so existing consumers that import from this module keep working.
export { ColumnConfigStore } from "./stores/column-config-store";
export type { ColumnConfig, CreateColumnConfigOptions } from "./stores/column-config-store";

// ────────────────────────────────────────────
// Context
// ────────────────────────────────────────────

const ColumnConfigContext = createContext<ColumnConfigStore | null>(null);

// ────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────

/**
 * Provides a `ColumnConfigStore` instance to the subtree.
 *
 * The store is **owned by the caller** (typically `SmartTableStore`) and
 * passed in as a prop.  This means column configs are scoped to the sheet
 * that owns the store and survive React re-renders/reconciliation — the
 * store's lifetime is tied to the sheet entry in `SmartTableStore`, not
 * to any React component's mount/unmount cycle.
 */
export const ColumnConfigProvider = ({
  store,
  children,
}: {
  store: ColumnConfigStore;
  children: ReactNode;
}) => <ColumnConfigContext.Provider value={store}>{children}</ColumnConfigContext.Provider>;

// ────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────

export const useColumnConfigStore = (): ColumnConfigStore => {
  const store = useContext(ColumnConfigContext);
  if (!store) {
    throw new Error("useColumnConfigStore must be used within a <ColumnConfigProvider>");
  }
  return store;
};
